import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import Button from "../../components/Buttons/Button";
import Loader from "../../components/Loaders/Loader";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { getPathDatabaseByRole } from "../../actions/auth";
import { getKeyDatabase } from "../../services/public";
import {
  startCreateEmptyCreativeTemplate,
  startDeleteCreativeTemplate,
  startDuplicateCreativeTemplate,
  startListenerCreativeTemplateCards,
  stopListenerCreativeTemplateCards,
} from "../../actions/creativeTemplates";
import { canWriteCreativeTemplates } from "../../features/creativeTemplates/utils/permissions";
import { ConfirmAlert, SimpleAlert } from "../../components/Alerts/Alerts";
import { canProduceCreativeTemplate } from "../../features/creativeTemplates/utils/layerTree";
import { getArtboardChipLabel } from "../../features/creativeTemplates/utils/artboards";
import { ALERT_ICON_TYPE_SUCCESS } from "../../utils/constants";
import {
  BUTTON_CLOSE,
  LOADING_INFORMATION,
  TEMPLATES_BUTTON_CREATE,
  TEMPLATES_CONFIRM_DELETE_DESCRIPTION,
  TEMPLATES_CONFIRM_DELETE_TITLE,
  TEMPLATES_CREATE_ERROR,
  TEMPLATES_DELETE,
  TEMPLATES_DELETE_SUCCESS,
  TEMPLATES_DESCRIPTION,
  TEMPLATES_DUPLICATE,
  TEMPLATES_EMPTY,
  TEMPLATES_ARTBOARD,
  TEMPLATES_NODE_COUNT,
  TEMPLATES_PRODUCE_TITLE,
  TEMPLATES_TITLE,
  TEMPLATES_UNAUTHORIZED,
  TEMPLATES_UNTITLED,
} from "../../i18n/keysTranslations";

const Templates = ({
  onOpenEditor = () => {},
  onOpenProduce = () => {},
  onCloseModal,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const dispatch = useDispatchApp();
  const listenerRef = useRef(null);
  const { role } = dispatch(getPathDatabaseByRole());
  const canWrite = canWriteCreativeTemplates({ role });
  const [cards, setCards] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    dispatch(
      startListenerCreativeTemplateCards({
        onUpdatedDataCallback: (value) => {
          setCards(value || {});
          setIsLoading(false);
        },
        onReferenceAvailable: (dbRef) => {
          listenerRef.current = dbRef;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerCreativeTemplateCards({ listenerRef: listenerRef.current })
      );
    };
  }, [dispatch]);

  const onCreate = async () => {
    if (!canWrite) {
      SimpleAlert({
        title: t(TEMPLATES_UNAUTHORIZED),
        icon: "warning",
      });
      return;
    }
    setIsCreating(true);
    const response = await dispatch(
      startCreateEmptyCreativeTemplate({ name: t(TEMPLATES_UNTITLED) })
    );
    setIsCreating(false);
    if (!response.ok) {
      SimpleAlert({
        title: t(TEMPLATES_CREATE_ERROR),
        icon: "error",
      });
      return;
    }
    onOpenEditor({ templateID: response.data.templateID });
  };

  const onDuplicate = async ({ templateID }) => {
    const response = await dispatch(
      startDuplicateCreativeTemplate({ templateID })
    );
    if (response.ok) {
      onOpenEditor({ templateID: response.data.templateID });
    }
  };

  const onDelete = async ({ templateID }) => {
    const confirmed = await ConfirmAlert({
      title: t(TEMPLATES_CONFIRM_DELETE_TITLE),
      text: t(TEMPLATES_CONFIRM_DELETE_DESCRIPTION),
    });
    if (!confirmed.isConfirmed) return;
    const response = await dispatch(
      startDeleteCreativeTemplate({ templateID })
    );
    if (response.ok) {
      SimpleAlert({
        title: t(TEMPLATES_DELETE_SUCCESS),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
    }
  };

  const list = Object.values(cards).sort(
    (a, b) =>
      (b.updateTime || b.updatedAt || 0) - (a.updateTime || a.updatedAt || 0)
  );

  return (
    <ContainerPage hasMarginBottom={false} sx={{ height: "100%", pt: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          width: "100%",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <TitlePage>{t(TEMPLATES_TITLE)}</TitlePage>
          <Typography color="text.secondary">{t(TEMPLATES_DESCRIPTION)}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {canWrite ? (
            <Button color="primary" onClick={onCreate} loading={isCreating}>
              {t(TEMPLATES_BUTTON_CREATE)}
            </Button>
          ) : null}
          {onCloseModal ? (
            <IconButton
              className="close-modal-button"
              aria-label={t(BUTTON_CLOSE)}
              onClick={onCloseModal}
              sx={{
                p: 0.5,
                fontSize: "1.4rem",
                color: "background.paperSecondaryContrastText",
                backgroundColor: "background.paperSecondary",
                "&:hover": {
                  color: "background.paperSecondaryContrastText",
                  backgroundColor: "background.paperSecondary",
                  filter: theme.palette.hover.main,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </Box>
      </Box>
      {isLoading ? (
        <Loader hasMessage message={t(LOADING_INFORMATION)} />
      ) : list.length === 0 ? (
        <Typography color="text.secondary">{t(TEMPLATES_EMPTY)}</Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(212px, 1fr))",
            gap: 2,
            width: "100%",
          }}
        >
          {list.map((card) => (
            <Box
              key={card.id}
              sx={{
                border: "1px solid #E4E4E7",
                borderRadius: "14px",
                p: 1.25,
                bgcolor: "#fff",
              }}
            >
              <Box
                onClick={() => onOpenEditor({ templateID: card.id })}
                sx={{
                  width: "100%",
                  aspectRatio: "1",
                  borderRadius: "9px",
                  backgroundColor: "#EEE",
                  backgroundImage: card.thumbnailUrl
                    ? `url(${card.thumbnailUrl})`
                    : "none",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  cursor: "pointer",
                }}
              />
              <Typography sx={{ mt: 1, fontWeight: 500 }} noWrap>
                {card.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(card.artboardIDs?.length
                  ? card.artboardIDs
                  : [card.artboard?.id].filter(Boolean)
                )
                  .map((artboardID) =>
                    getArtboardChipLabel({
                      artboardID,
                      cardArtboard: card.artboard,
                    })
                  )
                  .join(" · ") ||
                  t(TEMPLATES_ARTBOARD, {
                    width: card.artboard?.width || 0,
                    height: card.artboard?.height || 0,
                  })}{" "}
                · {t(TEMPLATES_NODE_COUNT, { count: card.nodeCount || 0 })}
              </Typography>
              {canWrite ? (
                <Box sx={{ display: "flex", gap: 0.75, mt: 1.25 }}>
                  <Button
                    color="secondary"
                    onClick={() => onDelete({ templateID: card.id })}
                    sx={{ minWidth: 0, flex: 1, height: 36 }}
                  >
                    {t(TEMPLATES_DELETE)}
                  </Button>
                  <Button
                    color="secondary"
                    onClick={() => onDuplicate({ templateID: card.id })}
                    sx={{ minWidth: 0, flex: 1, height: 36 }}
                  >
                    {t(TEMPLATES_DUPLICATE)}
                  </Button>
                </Box>
              ) : null}
              {canProduceCreativeTemplate({ nodeCount: card.nodeCount }) ? (
                <Button
                  color="primary"
                  onClick={() => {
                    const produceID = dispatch(getKeyDatabase());
                    onOpenProduce({
                      templateID: card.id,
                      produceID,
                    });
                  }}
                  sx={{ minWidth: 0, width: "100%", mt: 0.75, height: 36 }}
                >
                  {t(TEMPLATES_PRODUCE_TITLE)}
                </Button>
              ) : null}
            </Box>
          ))}
        </Box>
      )}
    </ContainerPage>
  );
};

export default Templates;
